import { countConditions } from "@estelle/domain";
import type {
  AuditEntry,
  Group,
  Id,
  InviteResult,
  ListQuery,
  Page,
  Permission,
  PermissionKey,
  Role,
  SettingsRepository,
  Tag,
  User,
  WriteResult,
} from "@estelle/domain";

import { apiFetch } from "@/adapters/api/base";
import { createApiSync } from "@/adapters/api/sync";

/**
 * User, role and group management, over HTTP (FR-ACL-2 … FR-ACL-4, FR-ACL-6,
 * M10-7, M15-2, M15-3).
 *
 * `listAudit` and `getAudit` cross over here too (M15-6), against
 * `/api/settings/audit`.
 *
 * `getSyncStatus`, `listSyncEvents`, `runSync` (#154) and the channel write
 * log, `listChannelWrites` and `getChannelWrite` (#164, #173), are the part of
 * this file that is not `/api/settings` at all — they call `/api/sync`,
 * `apps/api`'s own route group — so they live behind `createApiSync()`
 * instead of the `get`/`write` helpers below, and are composed in here only
 * because `SettingsRepository` is where the Sync status page's methods live
 * (see that interface's doc comment in `@estelle/domain`).
 */

const BASE = "/api/settings";

function params(query: ListQuery | undefined): URLSearchParams {
  const out = new URLSearchParams();
  if (!query) return out;
  if (query.limit !== undefined) out.set("limit", String(query.limit));
  if (query.cursor !== undefined) out.set("cursor", query.cursor);
  if (query.search !== undefined && query.search !== "") {
    out.set("search", query.search);
  }
  if (query.filter && countConditions(query.filter) > 0) {
    out.set("filter", JSON.stringify(query.filter));
  }
  if (query.sort && query.sort.length > 0) {
    out.set(
      "sort",
      query.sort.map((entry) => `${entry.column}:${entry.direction}`).join(","),
    );
  }
  return out;
}

async function get<T>(path: string, query?: ListQuery): Promise<T> {
  const search = params(query).toString();
  const response = await apiFetch(
    `${BASE}${path}${search ? `?${search}` : ""}`,
  );
  if (!response.ok) {
    throw new Error(
      `Settings request failed: ${String(response.status)} on ${path || "/"}`,
    );
  }
  return (await response.json()) as T;
}

/**
 * A write's outcome. A refusal arrives as a `WriteResult` body with a 4xx
 * status — a rejection to show, not a failure to throw. Mirrors
 * `adapters/api/savedViews.ts`'s `write`.
 */
async function write<T>(
  path: string,
  method: "POST" | "PATCH" | "DELETE",
  body?: unknown,
): Promise<WriteResult<T>> {
  const response = await apiFetch(`${BASE}${path}`, {
    method,
    headers:
      body === undefined ? undefined : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const result = (await response
    .json()
    .catch(() => null)) as WriteResult<T> | null;
  if (result !== null && typeof result === "object" && "ok" in result) {
    return result;
  }
  throw new Error(
    `Settings ${method} failed: ${String(response.status)} on ${path || "/"}`,
  );
}

export function createApiSettings(): SettingsRepository {
  const sync = createApiSync();

  return {
    listUsers: (query) => get<Page<User>>("/users", query),

    listRoles: (query) => get<Page<Role>>("/roles", query),

    listPermissions: () => get<Permission[]>("/permissions"),

    inviteUser: (input) => write<InviteResult>("/users", "POST", input),

    resendInvite: (id) =>
      write<InviteResult>(
        `/users/${encodeURIComponent(id)}/resend-invite`,
        "POST",
      ),

    setUserRoles: (id, roleIds) =>
      write<User>(`/users/${encodeURIComponent(id)}/roles`, "PATCH", {
        roleIds,
      }),

    suspendUser: (id) =>
      write<User>(`/users/${encodeURIComponent(id)}/suspend`, "POST"),

    reactivateUser: (id) =>
      write<User>(`/users/${encodeURIComponent(id)}/reactivate`, "POST"),

    createRole: (input) => write<Role>("/roles", "POST", input),

    renameRole: (id, name) =>
      write<Role>(`/roles/${encodeURIComponent(id)}/name`, "PATCH", { name }),

    setRolePermissions: (id, perms: PermissionKey[]) =>
      write<Role>(`/roles/${encodeURIComponent(id)}/permissions`, "PATCH", {
        permissions: perms,
      }),

    deleteRole: async (id) => {
      const result = await write<null>(
        `/roles/${encodeURIComponent(id)}`,
        "DELETE",
      );
      return result.ok ? { ok: true, data: undefined } : result;
    },

    listGroups: (query) => get<Page<Group>>("/groups", query),

    createGroup: (name, description) =>
      write<Group>("/groups", "POST", { name, description }),

    renameGroup: (id: Id, name) =>
      write<Group>(`/groups/${encodeURIComponent(id)}/name`, "PATCH", {
        name,
      }),

    deleteGroup: async (id: Id) => {
      const result = await write<null>(
        `/groups/${encodeURIComponent(id)}`,
        "DELETE",
      );
      return result.ok ? { ok: true, data: undefined } : result;
    },

    setGroupMembers: (id: Id, userIds) =>
      write<Group>(`/groups/${encodeURIComponent(id)}/members`, "PATCH", {
        userIds,
      }),

    setGroupRoles: (id: Id, roleIds) =>
      write<Group>(`/groups/${encodeURIComponent(id)}/roles`, "PATCH", {
        roleIds,
      }),

    listChannelWrites: sync.listChannelWrites,
    getChannelWrite: sync.getChannelWrite,

    listAudit: (query) => get<Page<AuditEntry>>("/audit", query),
    getAudit: (id: Id) =>
      get<AuditEntry | null>(`/audit/${encodeURIComponent(id)}`),

    listTags: (query) => get<Page<Tag>>("/tags", query),

    renameTag: (id, name) =>
      write<Tag>(`/tags/${encodeURIComponent(id)}/name`, "PATCH", { name }),

    mergeTags: (sourceId, targetId) =>
      write<Tag>(`/tags/${encodeURIComponent(sourceId)}/merge`, "POST", {
        targetId,
      }),

    deleteTag: async (id) => {
      const result = await write<null>(
        `/tags/${encodeURIComponent(id)}`,
        "DELETE",
      );
      return result.ok ? { ok: true, data: undefined } : result;
    },

    getSyncStatus: sync.getSyncStatus,
    listSyncEvents: sync.listSyncEvents,
    runSync: sync.runSync,
  };
}
